import {
  AnyAction,
  Store,
  StoreEnhancer,
  applyMiddleware,
  compose,
  createStore,
} from "redux";
import createSagaMiddleware from "redux-saga";
import thunk from "redux-thunk";

import type { OptionalReducers } from "./buildRootReducer";
import { buildRootReducer } from "./buildRootReducer";
import type { SagaEffect } from "./buildRootSaga";
import { buildRootSaga } from "./buildRootSaga";
import { globalReducers } from "./globalReducers";
import { globalPageReducers } from "./ui-state";

type PreloadedState = Parameters<typeof createStore>[1];

interface BuildApplicationStoreOptions {
  appReducers?: OptionalReducers;
  pageReducers?: OptionalReducers;
  appSagas?: SagaEffect[];
  pageSagas?: SagaEffect[];
  debug?: boolean;
  preloadedState?: PreloadedState;
}

interface ReduxDevToolsOptions {
  name: string;
}

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: (
      options: ReduxDevToolsOptions,
    ) => StoreEnhancer;
  }
}

/**
 * Function to build the Redux application store.
 *
 * @param {Object} [options] - an optional store configuration object.
 * @param {Object} [options.appReducers=null] - An object of app state reducers.
 * @param {Object} [options.pageReducers=null] - An object of UI state reducers.
 * @param {generator[]} [options.appSagas=[]] - An array of app saga generators.
 * @param {generator[]} [options.pageSagas=[]] - An array of UI saga generators.
 * @param {Boolean} [options.debug=false] - A flag to expose Redux devtools.
 * @param {Object} [options.preloadedState] - An optional initial state for the store.
 *
 * @return {Object} - Returns the application store.
 */
function buildApplicationStore(
  options: BuildApplicationStoreOptions = {},
): Store<unknown, AnyAction> {
  const {
    appReducers = null,
    pageReducers = null,
    appSagas = [],
    pageSagas = [],
    debug = false,
    preloadedState,
  } = options;
  const rootSaga = buildRootSaga(appSagas, pageSagas);
  const sagaMiddleware = createSagaMiddleware();

  const devtoolsEnhancer =
    debug &&
    window.__REDUX_DEVTOOLS_EXTENSION__ &&
    window.__REDUX_DEVTOOLS_EXTENSION__({
      name: `${window.location.host} (${Date.now()})`,
    });

  const middlewareEnhancer = applyMiddleware(thunk, sagaMiddleware);

  const enhancer = devtoolsEnhancer
    ? compose(devtoolsEnhancer, middlewareEnhancer)
    : middlewareEnhancer;

  const allPageReducers = {
    ...(pageReducers || {}),
    ...globalPageReducers,
  };

  const store = createStore(
    buildRootReducer(globalReducers, appReducers, allPageReducers),
    preloadedState,
    enhancer,
  );

  sagaMiddleware.run(rootSaga);

  return store;
}

export type { BuildApplicationStoreOptions };
export { buildApplicationStore };
